import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { withUnicodeRouteArrow } from '../../../utils/routeLabelDisplay'
import {
  ChartValueFormat,
  formatChartDataLabelValue,
} from '../common/chartFormat'
import {
  FLOATING_CHART_TOOLTIP_CURSOR_OFFSET_PX,
  useChartTooltipPointer,
} from './ChartTooltipPointerContext'
import { FloatingTooltipKvRow } from './FloatingTooltipKvRow'

export type FloatingPieChartTooltipPayload = {
  active?: boolean
  payload?: any[]
}

const clampTooltipPosition = (
  left: number,
  top: number,
  width: number,
  height: number,
) => {
  const pad = 8
  const maxL = Math.max(pad, window.innerWidth - width - pad)
  const maxT = Math.max(pad, window.innerHeight - height - pad)
  return {
    left: Math.min(Math.max(pad, left), maxL),
    top: Math.min(Math.max(pad, top), maxT),
  }
}

interface FloatingPieChartTooltipProps extends FloatingPieChartTooltipPayload {
  total: number
  nameKey: string
  /** Per-chart numeric formatting for slice + "Other" values. */
  valueFormat?: ChartValueFormat
  /** Label for the slice value row (e.g. "Container Count"). Defaults to "Value". */
  valueLabel?: string
}

export const FloatingPieChartTooltip = ({
  active,
  payload,
  total,
  nameKey,
  valueFormat,
  valueLabel = 'Value',
}: FloatingPieChartTooltipProps) => {
  const pointer = useChartTooltipPointer()
  const panelRef = useRef<HTMLDivElement>(null)
  const [adjusted, setAdjusted] = useState<{
    left: number
    top: number
  } | null>(null)

  const body = useMemo(() => {
    if (!active || !payload?.length) return null
    const entry = payload[0]
    const pl = entry.payload as Record<string, unknown>
    const name = withUnicodeRouteArrow(String(entry.name ?? pl[nameKey] ?? ''))
    const val = Number(entry.value) ?? 0
    const color =
      (typeof pl.fill === 'string' && pl.fill) ||
      (typeof entry.color === 'string' && entry.color) ||
      '#fff'
    const pct = total > 0 ? Math.round((val / total) * 100) : 0
    const isOther = Boolean(pl._isOther)
    const otherItems = (pl._otherItems ?? []) as {
      name: string
      value: number
    }[]

    return { name, val, color, pct, isOther, otherItems }
  }, [active, nameKey, payload, total])

  const draftLeft = pointer
    ? pointer.clientX + FLOATING_CHART_TOOLTIP_CURSOR_OFFSET_PX
    : 0
  const draftTop = pointer
    ? pointer.clientY + FLOATING_CHART_TOOLTIP_CURSOR_OFFSET_PX
    : 0

  useLayoutEffect(() => {
    if (!body || !pointer) {
      setAdjusted(null)
      return
    }
    const el = panelRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const next = clampTooltipPosition(
      draftLeft,
      draftTop,
      rect.width,
      rect.height,
    )
    setAdjusted(prev =>
      prev?.left === next.left && prev?.top === next.top ? prev : next,
    )
  }, [body, draftLeft, draftTop, pointer])

  if (!body || !pointer) return null

  const { name, val, color, pct, isOther, otherItems } = body
  const left = adjusted?.left ?? draftLeft
  const top = adjusted?.top ?? draftTop

  const panel = (
    <Box
      ref={panelRef}
      data-testid="floating-chart-tooltip"
      position="fixed"
      left={`${left}px`}
      top={`${top}px`}
      zIndex={10000}
      bg="presets.overlay.turquoise100_95"
      borderRadius={0}
      px="12px"
      py="10px"
      minW="220px"
      maxW="300px"
      boxShadow="0 4px 16px rgba(0,0,0,0.35)"
    >
      <VStack align="stretch" spacing="6px" alignItems="flex-start">
        <HStack spacing="8px" align="center" w="100%">
          <Box
            w="14px"
            h="14px"
            flexShrink={0}
            borderRadius="full"
            bg={color}
            border="2px solid white"
            boxShadow="0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)"
          />
          <Text color="white" fontWeight="700" fontSize="13px" noOfLines={2}>
            {name}
          </Text>
        </HStack>
        <Box w="100%">
          <FloatingTooltipKvRow
            layout="spaceBetween"
            label={valueLabel}
            value={formatChartDataLabelValue(val, valueFormat)}
          />
        </Box>
        <Box w="100%">
          <FloatingTooltipKvRow
            layout="spaceBetween"
            label="Percent of Total"
            value={`${pct}%`}
          />
        </Box>
        {isOther && otherItems.length > 0 ? (
          <>
            <Box h="1px" w="100%" bg="presets.neutral.20" />
            <VStack
              align="stretch"
              spacing="4px"
              maxH="200px"
              overflowY="auto"
              w="100%"
              alignItems="flex-start"
            >
              {otherItems.map((row, i) => (
                <Box key={`${row.name}-${i}`} w="100%">
                  <FloatingTooltipKvRow
                    layout="spaceBetween"
                    label={withUnicodeRouteArrow(row.name)}
                    value={formatChartDataLabelValue(row.value, valueFormat)}
                  />
                </Box>
              ))}
            </VStack>
          </>
        ) : null}
      </VStack>
    </Box>
  )

  return createPortal(panel, document.body)
}
